import { coreQueryKey } from '@/api/core/authorization';
import { readUnitSalesPage, type UnitSaleListItem } from '@/api/core/unit-sale';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import {
  Alert,
  AlertDescription,
  Button,
  DataTable,
  type DataTableColumn,
} from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { nativeCatalogFloorsQuery } from './native-floors-queries';
import { formatDate, known, reasonLabels, statusLabels } from './native-room-sales-section';
import { hasRoomSalesReadAccess, type RoomSalesRuntime } from './room-sales-queries';
import {
  DEFAULT_SALES_ORDER,
  emptyFilteredTitle,
  SalesOrderControls,
  salesOrderLabel,
  type SalesOrder,
} from './sales-order-controls';

/* eslint-disable @mgrvtechgroup/admin-ds/no-hand-rolled-screen -- 기존 객실 판매 현황과 같이 속지 상세 화면 안에 끼워 넣는 읽기 전용 구획이다. */

/**
 * 슬롯(Unit)별 판매 현황 목록. 로컬 BE candidate(010, 미배포)의 room-sales sale_scope=UNIT 계약만 쓴다:
 * 정렬은 floor|name, 방향은 asc|desc, 필터는 층(int32)이고 전체 개수·검색은 서버가 주지 않으므로 보이지 않는다.
 * 미배포 후보이므로 사용자가 열기 전에는 서버를 호출하지 않는다.
 */
const PAGE_SIZE = 20;
const money = (value: number | null) =>
  value === null
    ? '미설정(상속)'
    : value === 0
      ? '0원(명시 면제)'
      : `${value.toLocaleString('ko-KR')}원`;

function failureText(error: unknown) {
  if (isApiError(error) && error.status === 401)
    return '로그인이 필요합니다. 로그인 상태를 확인한 뒤 다시 조회해 주세요.';
  if (isApiError(error) && error.status === 403)
    return '현재 계정의 슬롯 판매 현황 조회 권한을 확인할 수 없습니다.';
  if (isApiError(error) && error.status === 404)
    return '지점을 찾을 수 없거나 이 서버가 슬롯 단위 판매 현황을 지원하지 않습니다.';
  if (isApiError(error) && error.status === 422)
    return '조회 조건을 서버가 받아들이지 않았습니다. 조건을 바꿔 다시 조회해 주세요.';
  return '슬롯 판매 현황 정보를 확인하지 못했습니다. 잠시 후 다시 조회해 주세요.';
}

/**
 * 표가 화면보다 넓을 때만 키보드로 닿을 수 있는 가로 스크롤 영역(이름 있는 region)과 안내를 둔다.
 * 넓은 화면에서는 초점 정지점도 안내도 만들지 않는다. 방향키는 영역 자체에 초점이 있을 때만 가로로 민다.
 */
function ScrollableTable({ label, children }: { label: string; children: ReactNode }) {
  const wrapper = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);
  useEffect(() => {
    const box = wrapper.current?.querySelector<HTMLElement>('[data-slot="table-container"]');
    if (!box) return;
    const update = () => {
      setOverflow(box.scrollWidth > box.clientWidth + 1);
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(box);
    return () => {
      observer.disconnect();
    };
  });
  return (
    <div
      ref={wrapper}
      role={overflow ? 'region' : undefined}
      aria-label={overflow ? label : undefined}
      tabIndex={overflow ? 0 : undefined}
      className="focus-visible:ring-ring rounded-md focus-visible:ring-2 focus-visible:outline-none"
      onKeyDown={(event) => {
        if (!overflow || event.target !== event.currentTarget) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const box = wrapper.current?.querySelector<HTMLElement>('[data-slot="table-container"]');
        if (!box) return;
        event.preventDefault();
        box.scrollBy({ left: event.key === 'ArrowRight' ? 80 : -80 });
      }}
    >
      {overflow && (
        <p className="caption text-muted-foreground">
          표가 화면보다 넓습니다. 좌우로 밀거나, 표에 초점을 두고 좌우 방향키로 이동하세요.
        </p>
      )}
      {children}
    </div>
  );
}

export function NativeUnitSalesSection({
  readEnabled = true,
  runtime,
  propertyId,
  generation,
  isCurrent,
  onSelectRoom,
  onOpenSlot,
}: {
  readEnabled?: boolean;
  runtime: RoomSalesRuntime;
  propertyId: string;
  generation: number;
  isCurrent: () => boolean;
  onSelectRoom?: (roomId: string) => void;
  /** 그 슬롯의 패널(가격·첫 입주일·전환)을 바로 연다. */
  onOpenSlot?: (roomId: string, unitId: string) => void;
}) {
  const session = useOperatorSession(runtime.session);
  const [opened, setOpened] = useState(false);
  const [order, setOrder] = useState<SalesOrder>(DEFAULT_SALES_ORDER);
  const { sortBy, sortDirection, floor, floorIsNull, q } = order;
  const [cursor, setCursor] = useState<string | null>(null);
  const [previousCursors, setPreviousCursors] = useState<(string | null)[]>([]);
  const allowed = hasRoomSalesReadAccess(runtime, generation) && session.generation === generation;
  const reset = () => {
    setCursor(null);
    setPreviousCursors([]);
  };
  const floorFacet = useQuery(
    nativeCatalogFloorsQuery(
      runtime,
      generation,
      propertyId,
      null,
      opened && readEnabled && allowed,
    ),
  );
  const list = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'unit-sales-read', propertyId),
      sortBy,
      sortDirection,
      floor,
      floorIsNull,
      q,
      cursor,
    ],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const page = await readUnitSalesPage(
        runtime.client,
        propertyId,
        { limit: PAGE_SIZE, cursor, sortBy, sortDirection, floor, floorIsNull, q },
        signal,
      );
      if (signal.aborted || !isCurrent() || !hasRoomSalesReadAccess(runtime, generation))
        throw new Error('현재 슬롯 판매 현황 조회 범위를 확인할 수 없습니다.');
      return page;
    },
    enabled: opened && readEnabled && allowed,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  });
  const columns = useMemo<DataTableColumn<UnitSaleListItem>[]>(
    () => [
      {
        key: 'unit',
        header: '객실 · 슬롯',
        sortable: false,
        cell: (item) => (
          <div className="min-w-0">
            <p className="label">{item.room_name}</p>
            <p className="caption text-muted-foreground mt-1">
              {item.unit_name ?? '이름 미설정'} · {item.room_type_name}
            </p>
          </div>
        ),
      },
      {
        key: 'floor',
        header: '층',
        sortable: false,
        cell: (item) => <span>{item.floor === null ? '미설정' : String(item.floor)}</span>,
      },
      {
        key: 'status',
        header: '판매 상태 · 사유',
        sortable: false,
        cell: (item) => (
          <div className="min-w-0">
            <span className="body-sm inline-flex rounded border px-2 py-1">
              {known(statusLabels, item.status)}
            </span>
            <ul className="caption mt-1 space-y-1">
              {item.reasons.length ? (
                item.reasons.map((reason, index) => (
                  <li key={`${reason}-${String(index)}`}>{known(reasonLabels, reason)}</li>
                ))
              ) : (
                <li>추가 사유 없음</li>
              )}
            </ul>
          </div>
        ),
      },
      {
        key: 'first-date',
        header: '첫 입주일',
        sortable: false,
        cell: (item) => <span>{formatDate(item.first_move_in_date)}</span>,
      },
      {
        key: 'price',
        header: '일 가격 · 보증금 · 청소비',
        sortable: false,
        cell: (item) => (
          <div className="caption min-w-0">
            <p>
              일 가격{' '}
              {item.daily_price_minor === null
                ? '미설정'
                : `${item.daily_price_minor.toLocaleString('ko-KR')}원`}
            </p>
            <p>보증금 {money(item.deposit_amount_minor)}</p>
            <p>청소비 {money(item.cleaning_amount_minor)}</p>
          </div>
        ),
      },
      {
        key: 'residual',
        header: '남은 기존 예약',
        sortable: false,
        cell: (item) => <span>{String(item.residual_booking_count)}건</span>,
      },
      {
        key: 'open',
        header: '이동',
        sortable: false,
        cell: (item) => (
          <div className="flex flex-wrap gap-1">
            {onOpenSlot && (
              <Button
                size="sm"
                variant="outline"
                aria-label={`${item.room_name} ${item.unit_name ?? '슬롯'} 슬롯 열기`}
                onClick={() => {
                  onOpenSlot(item.room_id, item.unit_id);
                }}
              >
                슬롯 열기
              </Button>
            )}
            {onSelectRoom && (
              <Button
                size="sm"
                variant="outline"
                aria-label={`${item.room_name} ${item.unit_name ?? '슬롯'} 객실 열기`}
                onClick={() => {
                  onSelectRoom(item.room_id);
                }}
              >
                객실 열기
              </Button>
            )}
          </div>
        ),
      },
    ],
    [onSelectRoom, onOpenSlot],
  );
  if (runtime.salesReadEnabled !== true) return null;
  return (
    <section
      aria-labelledby="unit-sales-heading"
      className="mt-5 flex min-w-0 flex-col gap-3 border-t pt-5"
    >
      <div>
        <h3 id="unit-sales-heading" className="heading-xs">
          슬롯별 판매 현황
        </h3>
        <p className="body-sm text-muted-foreground mt-1">
          객실 안의 슬롯마다 판매 상태와 가격을 봅니다. 객실 단위 현황과 따로 조회하며, 서버가
          제공하는 정렬과 층 필터만 쓸 수 있습니다.
        </p>
      </div>
      {/* eslint-disable-next-line no-negated-condition -- 권한 없음은 독립 구획의 예외 분기다. */}
      {!allowed ? (
        <p role="status" className="body-sm text-muted-foreground">
          현재 계정에서 슬롯 판매 현황 조회 권한을 확인할 수 없습니다.
        </p>
      ) : // eslint-disable-next-line no-negated-condition -- 사용자가 열기 전에는 서버를 호출하지 않는 첫 분기다.
      !opened ? (
        <div>
          <Button
            variant="outline"
            onClick={() => {
              setOpened(true);
            }}
          >
            슬롯별 판매 현황 조회
          </Button>
        </div>
      ) : (
        <>
          <SalesOrderControls
            subject="슬롯"
            nameLabel="이름 순"
            searchPlaceholder="객실·슬롯명 검색"
            floors={{
              data: floorFacet.data,
              isError: floorFacet.isError,
              isFetching: floorFacet.isFetching,
              retry: () => void floorFacet.refetch(),
            }}
            order={order}
            onChange={(next) => {
              setOrder(next);
              reset();
            }}
          />
          {list.isError ? (
            <Alert variant="warning" role="status" aria-live="polite">
              <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
                <span>{failureText(list.error)}</span>
                <Button
                  variant="outline"
                  disabled={list.isFetching}
                  onClick={() => void list.refetch()}
                >
                  {list.isFetching ? '조회 중…' : '다시 조회'}
                </Button>
              </AlertDescription>
            </Alert>
          ) : (
            <ScrollableTable label="슬롯별 판매 현황 표">
              <DataTable
                columns={columns}
                data={list.data?.items ?? []}
                rowKey={(item) => item.unit_id}
                isLoading={list.isLoading}
                empty={{
                  title: emptyFilteredTitle(
                    order,
                    '슬롯 판매 현황',
                    '조건에 맞는 슬롯 판매 현황이 없습니다.',
                  ),
                }}
              />
            </ScrollableTable>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="caption text-muted-foreground" role="status">
              {list.isError
                ? '이 페이지를 불러오지 못했습니다'
                : `${String(list.data?.items.length ?? 0)}개 표시 · 현재 페이지 · 서버 제공 순서(${salesOrderLabel(order, '이름')})`}
            </span>
            <Button
              variant="outline"
              disabled={list.isFetching || previousCursors.length === 0}
              onClick={() => {
                const next = [...previousCursors];
                setPreviousCursors(next.slice(0, -1));
                setCursor(next.at(-1) ?? null);
              }}
            >
              이전 페이지
            </Button>
            <Button
              variant="outline"
              disabled={list.isFetching || list.isError || !list.data?.next_cursor}
              onClick={() => {
                if (!list.data?.next_cursor) return;
                setPreviousCursors((current) => [...current, cursor]);
                setCursor(list.data.next_cursor);
              }}
            >
              다음 페이지
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
